import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import UserForm from "../../components/UserForm";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";

export default function Signup() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    role: "student",
    phone: "",
    cid: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [disableTutorSignup, setDisableTutorSignup] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await api.get("/public/settings");
        const disabled = res.settings?.disableTutorSignup || false;
        setDisableTutorSignup(disabled);
        if (disabled && formData.role === "tutor") {
          setFormData((prev) => ({ ...prev, role: "student" }));
        }
      } catch (e) {
        // If settings cannot be loaded, default to current state
      }
    };
    loadSettings();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.password.length < 10) {
      setError("Password must be at least 10 characters");
      return;
    }

    setLoading(true);

    try {
      if (disableTutorSignup && formData.role === "tutor") {
        throw {
          error:
            "Tutor signups are currently disabled. Please contact an administrator.",
        };
      }

      const result = await signup({
        email: formData.email,
        password: formData.password,
        name: {
          first: formData.firstName,
          last: formData.lastName,
        },
        role: disableTutorSignup ? "student" : formData.role,
        phone: formData.phone,
        cid: formData.cid,
      });
      toast.success("Signup successful! Redirecting to dashboard...");
      const role =
        result?.user?.role || (disableTutorSignup ? "student" : formData.role);
      if (role === "student") {
        navigate("/student/dashboard");
      } else if (role === "tutor") {
        navigate("/tutor/dashboard");
      } else if (role === "admin") {
        navigate("/admin/dashboard");
      } else {
        navigate("/");
      }
    } catch (err) {
      const errorMsg = err.error || "Signup failed. Please try again.";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-text">
            Create your account
          </h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && <p className="text-red-500 text-center">{error}</p>}
          <UserForm
            formData={formData}
            onChange={handleChange}
            isAdminForm={false}
            isNewUser={true}
            disableTutorSignup={disableTutorSignup}
          />

          <Button type="submit" loading={loading} className="w-full">
            Sign up
          </Button>

          <div className="text-center">
            <Link to="/login" className="text-sm text-primary hover:opacity-80">
              Already have an account? Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
